import moment, { Moment, MomentInput } from "moment-timezone";
import { getCurrentServerTime } from "utils/date";
import { LessonSession } from "./model";
import { isAssessmentSession } from "./utils";

export const SESSION_STEP = 30;
export const SESSION_TIMESLOTS = 2;
export const BOOKING_LEAD_TIME_HOURS = 24;
export const MIN_HOURS_BEFORE_LESSON = 24;

const VALID_PERIOD_OUTSIDE_LESSON = 5;
const BEFORE_LESSON_COUNTING_DOWN_PERIOD = 30;
export const LESSON_DURATION = 60;
const VALID_FOR_POST_LESSON_FEEDBACK_PERIOD = 55;
const VALID_FOR_POST_ASSESSMENT_FEEDBACK_PERIOD = 40;

export function isLessonValidForJoining(scheduled: string) {
  // True if less than 5 mins before scheduled, or lesson is in progress (within scheduled + 1h).
  return getCurrentServerTime().isBetween(
    moment.utc(scheduled).subtract(VALID_PERIOD_OUTSIDE_LESSON, "minutes"),
    moment.utc(scheduled).add(LESSON_DURATION, "minutes")
  );
}

export function isLessonValidForCountingDown(scheduled: string) {
  // True if < 30 mins remain before scheduled.
  return getCurrentServerTime().isBetween(
    moment.utc(scheduled).subtract(BEFORE_LESSON_COUNTING_DOWN_PERIOD, "minutes"),
    moment.utc(scheduled)
  );
}

export function isLessonFinished(scheduled: string) {
  // True if the lesson's official end (scheduled + 60min) has passed.
  return getCurrentServerTime().isAfter(
    moment.utc(scheduled).add(LESSON_DURATION, "minutes")
  );
}

export function isLessonTimeValidForPostFeedback(lesson: LessonSession) {
  // True if > 55/40 mins have elapsed since scheduled time.
  const validPeriod = isAssessmentSession(lesson)
    ? VALID_FOR_POST_ASSESSMENT_FEEDBACK_PERIOD
    : VALID_FOR_POST_LESSON_FEEDBACK_PERIOD;
  return getCurrentServerTime().isAfter(
    moment.utc(lesson.scheduled).add(validPeriod, "minutes")
  );
}

// Moment format string for serializing UTC times to the backend.
// The backend parses with Python's datetime.fromisoformat(), which accepts the
// "+00:00" offset but rejects the "Z" suffix that moment.toISOString() produces.
// Use as: m.utc().format(BACKEND_DATETIME_FORMAT)
export const BACKEND_DATETIME_FORMAT = "YYYY-MM-DD[T]HH:mm:ss[+00:00]";

// Checks whether a selected lesson time satisfies the minimum booking lead time.
export function isSelectedTimeValid(lessonTime: MomentInput, timezoneId: string) {
  const now = moment.tz(timezoneId);
  return moment
    .tz(lessonTime, timezoneId)
    .isAfter(now.clone().add(MIN_HOURS_BEFORE_LESSON, "hours"));
}

// Checks whether a selected time is close enough to show the lead-time warning.
export function shouldShowSelectedTimeNotification(
  lessonTime: MomentInput,
  timezoneId: string
) {
  const now = moment.tz(timezoneId);
  const someTimeAfterNow = now.clone().add(MIN_HOURS_BEFORE_LESSON, "hours");
  const lessonMoment = moment.tz(lessonTime, timezoneId);
  return (
    lessonMoment.isSameOrAfter(now, "hour") &&
    lessonMoment.isSameOrBefore(someTimeAfterNow, "hour")
  );
}

export function isFreeCancellationWindow(scheduledDate: string, timezoneId: string) {
  const scheduled = moment.tz(scheduledDate, timezoneId);
  const oneHourFromNow = moment.tz(timezoneId).add(BOOKING_LEAD_TIME_HOURS, "hours");
  return scheduled.isAfter(oneHourFromNow);
}

export function getSessionMonthRange(month: Moment) {
  const from = month.clone().startOf("month");
  let to = month.clone().endOf("month");
  if (moment.utc().isBefore(to)) {
    to = moment.utc();
  }

  return { from, to };
}

// Checks whether participants' relative offsets differ from their summer baseline.
export function isOffsetDifferent(
  studentTimezoneId: string,
  instructorTimezoneId: string,
  scheduled: Moment
) {
  const referenceYear = scheduled.clone().utc().year();

  const currentStudentOffset = scheduled.clone().tz(studentTimezoneId).utcOffset();
  const summerStudentOffset = getJulyReferenceOffset(referenceYear, studentTimezoneId);

  const currentInstructorOffset = scheduled.clone().tz(instructorTimezoneId).utcOffset();
  const summerInstructorOffset = getJulyReferenceOffset(
    referenceYear,
    instructorTimezoneId
  );

  const currentOffsetDifference = currentStudentOffset - currentInstructorOffset;
  const summerOffsetDifference = summerStudentOffset - summerInstructorOffset;

  return currentOffsetDifference !== summerOffsetDifference;
}

// Gets the noon offset for July 1 in a timezone for a stable summer reference.
function getJulyReferenceOffset(year: number, timezoneId: string) {
  return moment.tz({ year, month: 6, date: 1, hour: 12 }, timezoneId).utcOffset();
}
